import { useMemo, useState, type FormEvent } from 'react';
import { useLocation } from 'wouter';
import {
  Warning,
  CheckCircle,
  Clock,
  Lock,
  SpinnerGap,
  ShieldCheck,
  Sparkle,
} from '@phosphor-icons/react';
import { useBranding } from '@/lib/branding/BrandingProvider';
import { useInviteLookup } from '@/lib/invite/useInviteLookup';
import { useAcceptInvite } from '@/lib/invite/useAcceptInvite';

const ROLE_LABEL: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manager',
  team_lead: 'Team Lead',
  recruiter: 'Recruiter',
  viewer: 'Viewer',
  employee: 'Employee',
  member: 'Member',
};

export default function AcceptInvitePage() {
  const token = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('token') ?? '';
  }, []);

  const brand = useBranding();
  const [, setLocation] = useLocation();
  const lookup = useInviteLookup(token);
  const accept = useAcceptInvite();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const invite = lookup.data;
  const expired =
    invite?.invite_expires_at && new Date(invite.invite_expires_at).getTime() < Date.now();
  const alreadyAccepted = invite?.invite_status === 'accepted';

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setFormError('Passwords do not match.');
      return;
    }
    try {
      await accept.mutateAsync({ token, password });
      setLocation('/');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-neurax-ink">
      <div className="absolute inset-0 bg-neurax-glow" />
      <div className="absolute inset-0 grid-bg opacity-30" />

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-5xl items-center justify-center px-4 py-10">
        <div className="w-full max-w-xl">
          <div className="mb-8 flex flex-col items-center gap-3">
            <img
              src={invite?.logo_url || brand.logoUrl}
              alt={invite?.company_name || brand.name}
              className="h-14 w-auto max-w-[240px] object-contain drop-shadow-[0_0_22px_rgba(91,124,255,0.4)]"
            />
            <p className="text-[11px] uppercase tracking-[0.22em] text-neurax-electric/80">
              {invite?.product_name || brand.productName}
            </p>
          </div>

          <div className="glass-panel p-8 shadow-glow-soft">
            {!token && <MissingTokenState />}

            {token && lookup.isLoading && <LoadingState />}

            {token && !lookup.isLoading && !invite && <InvalidTokenState />}

            {token && invite && expired && !alreadyAccepted && (
              <ExpiredState company={invite.company_name} />
            )}

            {token && invite && alreadyAccepted && (
              <AlreadyAcceptedState onGo={() => setLocation('/')} company={invite.company_name} />
            )}

            {token && invite && !expired && !alreadyAccepted && (
              <>
                <div className="mb-6 flex items-center gap-2 text-xs text-neurax-electric">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  <span className="uppercase tracking-[0.2em]">Secure invitation</span>
                </div>

                <h1 className="font-display text-2xl font-semibold text-white">
                  Welcome, {invite.full_name.split(' ')[0]} 👋
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  You've been invited to join{' '}
                  <strong className="text-white">{invite.company_name}</strong> on{' '}
                  {invite.product_name || brand.productName} as a{' '}
                  <strong className="text-neurax-electric">
                    {ROLE_LABEL[invite.role] || invite.role}
                  </strong>
                  .
                </p>

                <div className="mt-5 grid gap-3 rounded-xl border border-white/5 bg-white/[0.03] p-4 text-sm">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">
                      Email
                    </span>
                    <span className="truncate text-white">{invite.email}</span>
                  </div>
                  {invite.invite_expires_at && (
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground">
                        Expires
                      </span>
                      <span className="text-white">
                        {new Date(invite.invite_expires_at).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                  )}
                </div>

                <form onSubmit={onSubmit} className="mt-7 space-y-4">
                  <PasswordField
                    label="Set your password"
                    value={password}
                    onChange={setPassword}
                    autoFocus
                  />
                  <PasswordField label="Confirm password" value={confirm} onChange={setConfirm} />

                  {formError && (
                    <div
                      role="alert"
                      className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
                    >
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={accept.isPending}
                    className="group relative inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-neurax-electric to-neurax-plasma px-5 py-3 text-sm font-semibold text-white shadow-glow transition hover:brightness-110 disabled:opacity-60"
                  >
                    {accept.isPending ? (
                      <>
                        <SpinnerGap className="h-4 w-4 animate-spin" />
                        Creating your workspace…
                      </>
                    ) : (
                      <>
                        <Sparkle className="h-4 w-4" />
                        Accept invitation & sign in
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PasswordField({
  label,
  value,
  onChange,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      <div className="relative">
        <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="password"
          required
          minLength={8}
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="••••••••••••"
          className="w-full rounded-xl border border-white/10 bg-white/[0.04] py-3 pl-10 pr-4 text-sm text-white placeholder:text-muted-foreground/60 outline-none transition focus:border-neurax-electric/60 focus:bg-white/[0.06] focus:ring-2 focus:ring-neurax-electric/30"
        />
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-muted-foreground">
      <SpinnerGap className="h-6 w-6 animate-spin text-neurax-electric" />
      <p className="text-sm">Verifying your invitation…</p>
    </div>
  );
}

function MissingTokenState() {
  const { supportEmail } = useBranding();
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <div className="grid h-12 w-12 place-items-center rounded-2xl bg-destructive/10 text-destructive">
        <Warning className="h-5 w-5" />
      </div>
      <h2 className="font-display text-lg font-semibold text-white">Invitation link missing</h2>
      <p className="max-w-sm text-sm text-muted-foreground">
        This link doesn't include an invitation token. Ask your admin to resend it, or contact{' '}
        <a className="text-neurax-electric hover:underline" href={`mailto:${supportEmail}`}>
          {supportEmail}
        </a>
        .
      </p>
    </div>
  );
}

function InvalidTokenState() {
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <div className="grid h-12 w-12 place-items-center rounded-2xl bg-destructive/10 text-destructive">
        <Warning className="h-5 w-5" />
      </div>
      <h2 className="font-display text-lg font-semibold text-white">Invitation not found</h2>
      <p className="max-w-sm text-sm text-muted-foreground">
        The link may have been revoked. Ask your admin to send a new invitation.
      </p>
    </div>
  );
}

function ExpiredState({ company }: { company: string }) {
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <div className="grid h-12 w-12 place-items-center rounded-2xl bg-neurax-amber/10 text-neurax-amber">
        <Clock className="h-5 w-5" />
      </div>
      <h2 className="font-display text-lg font-semibold text-white">This invitation has expired</h2>
      <p className="max-w-sm text-sm text-muted-foreground">
        Ask an admin at {company} to send you a fresh invitation.
      </p>
    </div>
  );
}

function AlreadyAcceptedState({ onGo, company }: { onGo: () => void; company: string }) {
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <div className="grid h-12 w-12 place-items-center rounded-2xl bg-neurax-mint/10 text-neurax-mint">
        <CheckCircle className="h-5 w-5" />
      </div>
      <h2 className="font-display text-lg font-semibold text-white">
        You've already joined {company}
      </h2>
      <p className="max-w-sm text-sm text-muted-foreground">
        Sign in to continue to your workspace.
      </p>
      <button
        onClick={onGo}
        className="mt-2 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-neurax-electric to-neurax-plasma px-4 py-2.5 text-sm font-semibold text-white shadow-glow-soft transition hover:brightness-110"
      >
        Go to sign in
      </button>
    </div>
  );
}
